import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import ExtUITextInput from 'ext-ui/components/TextInput/TextInput'
import React, { FormEvent, ReactElement, useState } from 'react'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { toDollars } from 'src/utils/currency.utils'

import SettingsSegmented from '../SettingsSegmented/SettingsSegmented'
import SettingsTile from '../SettingsTile/SettingsTile'
import style from './SpendLimitTile.style'

type SpendLimitTileProps = {
  creditCard: CreditCardListModel
  /**
   * Admin/Owner. Everyone else's increases are routed to an Admin or Owner to
   * approve, so the form asks them to submit a request; an Admin/Owner is the
   * approver, and applies the change directly.
   */
  canApplyDirectly: boolean
}

type LimitKind = 'permanent' | 'temporary'

export default function SpendLimitTile(
  props: SpendLimitTileProps,
): ReactElement {
  const { creditCard, canApplyDirectly } = props
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()

  const [limitKind, setLimitKind] = useState<LimitKind>('permanent')
  const [limitAmount, setLimitAmount] = useState<number | undefined>(undefined)
  const [limitFocused, setLimitFocused] = useState(false)
  const [reason, setReason] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [amountError, setAmountError] = useState('')
  const [datesError, setDatesError] = useState('')

  // Show a formatted amount ($60,000) at rest and raw digits while editing —
  // the monetary-input pattern used across the app (see AmountInputField).
  const limitDisplayValue =
    limitAmount === undefined
      ? ''
      : limitFocused
        ? String(limitAmount)
        : formatCurrency({
            amount: limitAmount,
            currency: creditCard.currency,
            maximumFractionDigits: 0,
          })

  const handleLimitChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const digits = event.target.value.replace(/[^0-9]/g, '')
    setLimitAmount(digits === '' ? undefined : Number(digits))
    setAmountError('')
  }

  const limitCents =
    creditCard.issuingLimitCents ?? creditCard.issuerAccountCreditLimitCents
  // No invented fallback: when the card carries no limit we say so rather than
  // rendering a plausible-looking number the user could act on.
  const formattedLimit =
    limitCents !== undefined
      ? formatCurrency({
          amount: toDollars(limitCents),
          currency: creditCard.currency,
          maximumFractionDigits: 0,
        })
      : undefined

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault()

    const amountIsValid = limitAmount !== undefined && limitAmount > 0
    setAmountError(
      amountIsValid ? '' : translate('Enter a limit greater than zero.'),
    )

    const datesAreValid =
      limitKind === 'permanent' ||
      (startDate !== '' && endDate !== '' && startDate <= endDate)
    setDatesError(
      datesAreValid ? '' : translate('Enter a start and end date, in order.'),
    )

    if (!amountIsValid || !datesAreValid) {
      return
    }
    setSubmitted(true)
  }

  const resetForm = (): void => {
    setSubmitted(false)
    setLimitAmount(undefined)
    setLimitFocused(false)
    setReason('')
    setStartDate('')
    setEndDate('')
    setAmountError('')
    setDatesError('')
  }

  const limitKindOptions = [
    { label: translate('Permanent'), value: 'permanent' },
    { label: translate('Temporary'), value: 'temporary' },
  ]

  return (
    <SettingsTile title={translate('Spend limit')}>
      <div className="SpendLimitTile" css={style}>
        <div className="SpendLimitTile-kv">
          <span className="SpendLimitTile-kvLabel">
            {translate('Card limit')}
            {formattedLimit !== undefined && (
              <Pill size="small" color="highlight">
                {translate('Adjustable')}
              </Pill>
            )}
          </span>
          <span
            className="SpendLimitTile-kvValue"
            data-testid="spend-limit-current"
          >
            {formattedLimit ?? translate('Not available')}
          </span>
        </div>

        <hr className="SpendLimitTile-divider" />

        <h4 className="SpendLimitTile-heading">
          {translate('Adjust the spend limit')}
        </h4>
        <p className="SpendLimitTile-body">
          {canApplyDirectly
            ? translate(
                'Once connected: decreases apply instantly, and increases are applied within your company line and recorded in Activity.',
              )
            : translate(
                'Once connected: decreases apply instantly, and increases are governed — routed to an Admin or Owner to approve, then applied within your company line, and recorded in Activity.',
              )}
        </p>
        <p className="SpendLimitTile-body" data-testid="spend-limit-preview">
          {translate(
            'Limit changes are not connected yet. This form is a preview — nothing is sent and no limit changes.',
          )}
        </p>

        {submitted ? (
          <div className="SpendLimitTile-confirmation">
            <span className="SpendLimitTile-confirmationHeading">
              {canApplyDirectly
                ? translate('Nothing was applied')
                : translate('Nothing was submitted')}
            </span>
            <p className="SpendLimitTile-confirmationBody">
              {canApplyDirectly
                ? translate(
                    'This is a preview of the update flow. No change was made and the card limit is unchanged.',
                  )
                : translate(
                    'This is a preview of the request flow. No request was sent and the card limit is unchanged.',
                  )}
            </p>
            <Button
              variant="no-style"
              onClick={resetForm}
              data-testid="spend-limit-reset"
            >
              {translate('Start over')}
            </Button>
          </div>
        ) : (
          <form className="SpendLimitTile-form" onSubmit={handleSubmit}>
            <SettingsSegmented
              ariaLabel={translate('Limit duration')}
              options={limitKindOptions}
              value={limitKind}
              onChange={(next) => setLimitKind(next as LimitKind)}
            />
            <ExtUITextInput
              variant="small"
              label={
                limitKind === 'temporary'
                  ? translate('Temporary limit')
                  : translate('New limit')
              }
              placeholder={limitKind === 'temporary' ? '$75,000' : '$60,000'}
              value={limitDisplayValue}
              inputMode="numeric"
              requirement="required"
              errorText={amountError}
              onFocus={() => setLimitFocused(true)}
              onBlur={() => setLimitFocused(false)}
              onChange={handleLimitChange}
              data-testid="spend-limit-amount"
            />
            {limitKind === 'temporary' && (
              <div className="SpendLimitTile-dates">
                <ExtUITextInput
                  variant="small"
                  label={translate('Start')}
                  type="date"
                  requirement="required"
                  errorText={datesError}
                  value={startDate}
                  onChange={(event) => {
                    setStartDate(event.target.value)
                    setDatesError('')
                  }}
                />
                <ExtUITextInput
                  variant="small"
                  label={translate('End')}
                  type="date"
                  requirement="required"
                  value={endDate}
                  onChange={(event) => {
                    setEndDate(event.target.value)
                    setDatesError('')
                  }}
                />
              </div>
            )}
            <ExtUITextInput
              variant="small"
              label={translate('Reason')}
              placeholder={translate('Q3 travel & events budget')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <div>
              <Button
                type="submit"
                dropShadow={false}
                data-testid="spend-limit-submit"
              >
                {canApplyDirectly
                  ? translate('Update limit')
                  : translate('Submit request')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </SettingsTile>
  )
}
